import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { soundManager } from '../utils/soundManager';

interface GameTimerProps {
  timeLimit: number; // sekunder
  isActive: boolean;
  onTimeUp: () => void;
  onReset?: () => void;
  className?: string;
}

const pulseWarning = keyframes`
  0% { 
    transform: scale(1);
    color: #FF6B6B;
  }
  50% { 
    transform: scale(1.1);
    color: #FF3030;
    text-shadow: 0 0 20px #FF3030;
  }
  100% { 
    transform: scale(1);
    color: #FF6B6B;
  }
`;

const shake = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  50% { transform: translateX(2px); }
  75% { transform: translateX(-2px); }
  100% { transform: translateX(0); }
`;

const TimerContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 16px;
  color: white;
  min-width: 200px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const TimerDisplay = styled.div<{ $isWarning: boolean; $isTimeUp: boolean; $isFinal: boolean }>`
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 8px;
  color: ${props => {
    if (props.$isTimeUp) return '#FF3030';
    if (props.$isWarning) return '#FF6B6B';
    return '#4CAF50';
  }};
  
  ${props => props.$isWarning && css`
    animation: ${pulseWarning} 1s ease-in-out infinite;
  `}
  ${props => props.$isFinal && css`
    animation: ${shake} 0.4s ease-in-out infinite;
  `}
  
  transition: color 0.3s ease;
`;

const TimerLabel = styled.div`
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const TimerBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const TimerBar = styled.div<{ 
  $percentage: number; 
  $isWarning: boolean; 
  $isActive: boolean;
  $duration: number;
}>`
  height: 100%;
  background: ${props => {
    if (props.$percentage <= 20) return 'linear-gradient(90deg, #FF3030, #FF6B6B)';
    if (props.$percentage <= 50) return 'linear-gradient(90deg, #FFB74D, #FFA726)';
    return 'linear-gradient(90deg, #66BB6A, #4CAF50)';
  }};
  border-radius: 4px;
  transition: ${props => props.$isActive ? 'none' : 'width 0.3s ease'};
  width: ${props => props.$percentage}%;
  position: relative;
  

  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      transparent 100%
    );
    animation: ${props => props.$isActive ? css`
      shimmer 2s ease-in-out infinite
    ` : 'none'};
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const TimerStatus = styled.div<{ $status: 'ready' | 'running' | 'warning' | 'timeup' }>`
  font-size: 11px;
  margin-top: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  background: ${props => {
    switch (props.$status) {
      case 'ready': return 'rgba(76, 175, 80, 0.8)';
      case 'running': return 'rgba(33, 150, 243, 0.8)';
      case 'warning': return 'rgba(255, 152, 0, 0.8)';
      case 'timeup': return 'rgba(244, 67, 54, 0.8)';
    }
  }};
  text-transform: uppercase;
  font-weight: bold;
`;

// CHANGE (2026-02-23): "Återställ"-knapp borttagen enligt krav (inga rensa/återställ-knappar i appen).

const GameTimer: React.FC<GameTimerProps> = ({ 
  timeLimit, 
  isActive, 
  onTimeUp, 
  onReset,
  className 
}) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const startAtRef = useRef<number | null>(null);
  const onTimeUpRef = useRef(onTimeUp);
  const timeUpFiredRef = useRef(false);

  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  useEffect(() => {
    // CHANGE (2026-02-23): Fix för Blitz/Snabb-lägen.
    // Tidigare låg `isRunning` i dependency-listan vilket gjorde att cleanup kördes direkt
    // efter `setIsRunning(true)` och då stoppade intervallet (timern "frös").
    // Nu styrs intervallet endast av `isActive`/`timeLimit` så att exakt en timer körs.

    // Stoppa och städa när timern inte ska vara aktiv.
    if (!isActive) {
      setIsRunning(false);
      timeUpFiredRef.current = false;
      startAtRef.current = null;
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Starta (eller restart vid ny runda/timeLimit ändring)
    setIsRunning(true);
    timeUpFiredRef.current = false;
    setTimeLeft(timeLimit);
    startAtRef.current = Date.now();

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    intervalRef.current = window.setInterval(() => {
      if (!startAtRef.current) return;
      const elapsed = Math.floor((Date.now() - startAtRef.current) / 1000);
      const remain = Math.max(0, timeLimit - elapsed);
      setTimeLeft(remain);

      if (remain <= 0 && !timeUpFiredRef.current) {
        timeUpFiredRef.current = true;
        setIsRunning(false);
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        onTimeUpRef.current();
      }
    }, 250);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, timeLimit]);

  const formatTime = (seconds: number): string => {
    // Visa bara sekunder enligt användarkrav
    return `${seconds}s`;
  };

  const getPercentage = (): number => {
    return (timeLeft / timeLimit) * 100;
  };

  const getStatus = (): 'ready' | 'running' | 'warning' | 'timeup' => {
    if (timeLeft === 0) return 'timeup';
    if (timeLeft <= timeLimit * 0.2) return 'warning';
    if (isRunning) return 'running';
    return 'ready';
  };

  const getStatusText = (status: 'ready' | 'running' | 'warning' | 'timeup'): string => {
    switch (status) {
      case 'ready': return 'Redo att börja';
      case 'running': return 'Timer aktiv';
      case 'warning': return 'Tid håller på att ta slut!';
      case 'timeup': return 'Tiden är ute!';
    }
  };

  // CHANGE (2026-02-23): Återställ-funktion UI borttagen enligt krav.
  // (onReset finns kvar i props för bakåtkompabilitet, men används inte längre.)

  const percentage = getPercentage();
  const status = getStatus();
  const isWarning = status === 'warning';
  const isTimeUp = status === 'timeup';
  const isFinal = timeLeft > 0 && timeLeft <= 10;

  // Stress: ticking in last 10s
  const lastTickRef = useRef<number>(timeLeft);
  useEffect(() => {
    if (!isRunning) return;
    if (timeLeft <= 10 && timeLeft > 0 && timeLeft !== lastTickRef.current) {
      // pitch goes up as time decreases
  const pitch = 600 + (10 - timeLeft) * 30;
  soundManager.playTick(pitch, 0.08);
      lastTickRef.current = timeLeft;
      if ('vibrate' in navigator && timeLeft <= 3) {
        try { navigator.vibrate?.(50); } catch {}
      }
    }
  }, [timeLeft, isRunning]);

  return (
    <TimerContainer className={className}>
      <TimerLabel>⏱️ Timer</TimerLabel>
      
  <TimerDisplay $isWarning={isWarning} $isTimeUp={isTimeUp} $isFinal={isFinal}>
        {formatTime(timeLeft)}
      </TimerDisplay>

      <TimerBarContainer>
        <TimerBar
          $percentage={percentage}
          $isWarning={isWarning}
          $isActive={isRunning}
          $duration={timeLimit}
        />
      </TimerBarContainer>

      <TimerStatus $status={status}>
        {getStatusText(status)}
      </TimerStatus>

      {/* CHANGE (2026-02-23): "Återställ"-knapp borttagen. */}
    </TimerContainer>
  );
};

export default GameTimer;
